import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import WorkspaceOverview, { type WorkspaceMetric } from './dashboard/WorkspaceOverview';
import WorkspaceSection from './dashboard/WorkspaceSection';
import WorkspaceSectionGroup from './dashboard/WorkspaceSectionGroup';
import Button from './ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from './ui/Table';
import CollapseToggle from './dashboard/CollapseToggle';

const formatDate = (value?: string) => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const planoStatusLabel = (status?: string) => {
  switch (status) {
    case 'CONCLUIDO':
      return 'Concluído';
    case 'REPROVADO':
      return 'Reprovado';
    case 'DEVOLVIDO_CORRECAO':
      return 'Devolvido';
    case 'PENDENTE_RETIFICACAO':
      return 'Pendente Retificacao';
    case 'APROVADO':
      return 'Aprovado';
    case 'EM_ANALISE':
      return 'Em análise';
    default:
      return 'Rascunho';
  }
};

const planoStatusTone = (status?: string) => {
  switch (status) {
    case 'CONCLUIDO':
    case 'APROVADO':
      return 'border-success-line bg-success-soft text-success-text';
    case 'REPROVADO':
    case 'DEVOLVIDO_CORRECAO':
      return 'border-danger-line bg-danger-soft text-danger-text';
    case 'PENDENTE_RETIFICACAO':
      return 'border-orange-200 bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300';
    case 'EM_ANALISE':
      return 'border-warning-line bg-warning-soft text-warning-text';
    default:
      return 'border-line bg-surface-muted text-fg';
  }
};

const modalidadeLabel = (modalidade?: string) => {
  switch (modalidade) {
    case 'TERMO_COLABORACAO':
      return 'Termo de colaboracao';
    case 'TERMO_FOMENTO':
      return 'Termo de fomento';
    default:
      return modalidade || 'Não informada';
  }
};

type ProcuradoriaWorkspaceProps = {
  auxiliary?: boolean;
};

const ProcuradoriaWorkspace: React.FC<ProcuradoriaWorkspaceProps> = ({ auxiliary = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(() => new Set([
    'como-funciona',
    'atalhos',
  ]));

  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);
  const renderCollapseButton = (id: string) => (
    <CollapseToggle collapsed={isBoxCollapsed(id)} onToggle={() => toggleBox(id)} />
  );
  const fixedCardClass = 'flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border shadow-sm';
  const fixedCardBodyClass = 'min-h-0 flex-1 overflow-y-auto';

  const openAnaliseJuridica = (planoId: string) => {
    navigate(`/dashboard/plano/${planoId}/analise-juridica`);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await planoService.listAll().catch(() => []);
        if (!active) return;
        setPlanos(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Não foi possível carregar a área da Procuradoria Geral Municipal.');
        setPlanos([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const planosJuridico = useMemo(
    () => planos.filter((item) => item.etapaAtual === 'PROCURADORIA_JURIDICA' && ['EM_ANALISE', 'APROVADO', 'PENDENTE_RETIFICACAO'].includes(item.status || '')),
    [planos]
  );

  const planosAguardandoPrimeiraAnalise = useMemo(
    () => planosJuridico.filter((item) => !(item.parecerJuridico && item.parecerJuridico.trim())),
    [planosJuridico]
  );

  const planoPrioritario = planosAguardandoPrimeiraAnalise[0] || planosJuridico[0] || null;

  const planosAnaliseIniciada = useMemo(
    () => planosJuridico.filter((item) => Boolean(item.parecerJuridico && item.parecerJuridico.trim())),
    [planosJuridico]
  );

  const stats = useMemo(() => {
    const concluidos = planos.filter((item) => item.etapaAtual === 'CONVENIOS_FORMALIZACAO' || item.status === 'CONCLUIDO').length;
    const reprovados = planos.filter((item) => item.etapaAtual === 'PROCURADORIA_JURIDICA' && item.status === 'REPROVADO').length;
    const comParecer = planos.filter((item) => Boolean(item.parecerJuridico && item.parecerJuridico.trim())).length;
    return {
      naFila: planosJuridico.length,
      concluidos,
      reprovados,
      comParecer,
    };
  }, [planos, planosJuridico]);

  const cards: WorkspaceMetric[] = [
    { label: auxiliary ? 'Na fila para análise' : 'Na fila jurídica', value: stats.naFila, tone: 'warning' },
    { label: auxiliary ? 'Pareceres registrados' : 'Com parecer registrado', value: stats.comParecer, tone: 'progress' },
    { label: 'Encaminhados adiante', value: stats.concluidos, tone: 'success' },
    { label: 'Reprovados', value: stats.reprovados, tone: 'neutral' },
  ];

  const quickLinks = [
    {
      label: 'Fila PGM completa',
      description: 'Abrir a lista filtrada dos planos aguardando decisão da Procuradoria Geral Municipal.',
      onClick: () => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA'),
    },
    {
      label: 'Prestação de contas',
      description: 'Acessar a visao consolidada dos planos e seus desdobramentos.',
      onClick: () => navigate('/dashboard/gestao/prestacao-contas-planos'),
    },
  ];

  return (
    <div className="w-full space-y-6">
      <div>
        <WorkspaceOverview
          profile={auxiliary ? 'Auxiliar PGM' : 'Procuradoria Geral Municipal'}
          description={auxiliary
            ? 'Elaboração de pareceres e encaminhamento para supervisão.'
            : 'Análises jurídicas e decisões da Procuradoria.'}
          metrics={cards}
          loading={loading}
        />

        <div className={`mt-5 ${fixedCardClass} border-info-line bg-surface/90`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-sm font-semibold text-info-fg">{planoPrioritario ? 'Próximo da fila' : 'Ação principal'}</h2>
            {renderCollapseButton('acao-principal')}
          </div>
          {!isBoxCollapsed('acao-principal') && (
          <div className={`${fixedCardBodyClass} flex flex-col gap-4 px-5 pb-4 lg:flex-row lg:items-center lg:justify-between`}>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-fg">
                {planoPrioritario ? (planoPrioritario.titulo || 'Plano sem título') : 'Nenhum plano pendente na fila jurídica'}
              </h2>
              <p className="max-w-2xl text-sm leading-6 text-fg-muted">
                {planoPrioritario
                  ? 'O plano abre direto no bloco de decisão da Procuradoria Geral Municipal.'
                  : auxiliary
                    ? 'Os planos chegam aqui quando um procurador designa você como auxiliar no parecer.'
                    : 'Quando um plano chegar para a Procuradoria Geral Municipal, o atalho principal aparecerá aqui.'}
              </p>
              {planoPrioritario && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-line bg-surface-muted px-3 py-1 font-medium text-fg">
                    Plano: {planoPrioritario.titulo || 'Nao informado'}
                  </span>
                  <span className="rounded-full border border-line bg-surface-muted px-3 py-1 font-medium text-fg">
                    {planoPrioritario.parecerJuridico?.trim() ? 'Análise iniciada' : 'Aguardando primeira análise'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[280px]">
              <Button variant="primary" onClick={() => { if (planoPrioritario) { openAnaliseJuridica(planoPrioritario.id); return; } navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA'); }}>
                {planoPrioritario ? 'Abrir análise jurídica' : 'Abrir fila jurídica'}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}>
                Ver fila completa
              </Button>
            </div>
          </div>
          )}
        </div>

      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className={`${fixedCardClass} border-line bg-surface`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-fg">Como esta etapa funciona</h2>
            {renderCollapseButton('como-funciona')}
          </div>
          {!isBoxCollapsed('como-funciona') && (
          <div className={`${fixedCardBodyClass} grid gap-3 px-5 pb-4 md:grid-cols-3`}>
            <div className="rounded-2xl border border-line bg-surface-muted p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fg-muted">1. Receber</p>
              <p className="mt-2 text-sm leading-6 text-fg">O plano chega após aprovação da Comissão de Seleção.</p>
            </div>
            <div className="rounded-2xl border border-line bg-surface-muted p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fg-muted">2. Emitir parecer</p>
              <p className="mt-2 text-sm leading-6 text-fg">O parecer jurídico é o centro da decisão. O acesso deve levar direto a esse ponto.</p>
            </div>
            <div className="rounded-2xl border border-line bg-surface-muted p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fg-muted">3. Decidir</p>
              <p className="mt-2 text-sm leading-6 text-fg">A decisão possível aqui é aprovar para formalização ou reprovar.</p>
            </div>
          </div>
          )}
        </div>

        <div className={`${fixedCardClass} border-line bg-surface`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-fg">Atalhos</h2>
            {renderCollapseButton('atalhos')}
          </div>
          {!isBoxCollapsed('atalhos') && (
          <div className={`${fixedCardBodyClass} space-y-3 px-5 pb-4`}>
            {quickLinks.map((item) => (
              <Button variant="secondary" className="w-full" key={item.label} onClick={item.onClick}>
                <p className="text-sm font-semibold text-fg">{item.label}</p>
                <p className="mt-1 text-sm leading-6 text-fg-muted">{item.description}</p>
              </Button>
            ))}
          </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className={`${fixedCardClass} border-line bg-surface px-5 py-10 text-center text-sm text-fg-muted`}>Carregando fila jurídica...</div>
      ) : error ? (
        <div className={`${fixedCardClass} border-line bg-surface px-5 py-10 text-center text-sm text-danger-text`}>{error}</div>
      ) : planosJuridico.length === 0 ? (
        <div className={`${fixedCardClass} border-line bg-surface px-5 py-10 text-center text-sm text-fg-muted`}>{auxiliary
          ? 'Nenhum plano designado a você. Os planos aparecem aqui quando um procurador designa você como auxiliar.'
          : 'Nenhum plano aguardando parecer jurídico neste momento.'}</div>
      ) : (
        <WorkspaceSectionGroup>
          {planosAguardandoPrimeiraAnalise.length > 0 && (
            <WorkspaceSection
              color="amber"
              title="Aguardando primeira análise"
              count={planosAguardandoPrimeiraAnalise.length}
              actionLabel="Abrir fila completa"
              onAction={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}
              collapsed={isBoxCollapsed('fila-juridica-primeira')}
              onToggleCollapse={() => toggleBox('fila-juridica-primeira')}
            >
              <div className="overflow-x-auto border-t border-slate-100">
                <Table density="comfortable">
                  <Thead>
                    <Tr className="border-b border-line">
                      <Th className="text-fg">Plano</Th>
                      <Th className="text-fg">Emenda</Th>
                      <Th className="text-fg">Modalidade</Th>
                      <Th className="text-fg">Status</Th>
                      <Th className="text-fg">Atualização</Th>
                      <Th align="right" className="text-fg">Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {planosAguardandoPrimeiraAnalise.slice(0, 8).map((item) => (
                      <Tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                        <Td>
                          <div className="font-semibold text-fg">{item.titulo || 'Não informado'}</div>
                          <div className="text-xs text-fg-muted">{item.numeroProtocolo || 'Sem protocolo'}</div>
                        </Td>
                        <Td className="text-fg">{item.emendaCodigo || 'Não informada'}</Td>
                        <Td className="text-fg">{modalidadeLabel(item.modalidade)}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${planoStatusTone(item.status)}`}>
                            {planoStatusLabel(item.status)}
                          </span>
                        </Td>
                        <Td className="text-fg">{formatDate(item.dataAtualizacao || item.aprovadoEm || item.enviadoEm)}</Td>
                        <Td align="right">
                          <Button variant="info" size="sm" onClick={() => openAnaliseJuridica(item.id)}>
                            Iniciar análise
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </div>
            </WorkspaceSection>
          )}

          {planosAnaliseIniciada.length > 0 && (
            <WorkspaceSection
              color="indigo"
              title="Análise iniciada"
              count={planosAnaliseIniciada.length}
              actionLabel="Abrir fila completa"
              onAction={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}
              collapsed={isBoxCollapsed('fila-juridica-iniciada')}
              onToggleCollapse={() => toggleBox('fila-juridica-iniciada')}
            >
              <div className="overflow-x-auto border-t border-slate-100">
                <Table density="comfortable">
                  <Thead>
                    <Tr className="border-b border-line">
                      <Th className="text-fg">Plano</Th>
                      <Th className="text-fg">Emenda</Th>
                      <Th className="text-fg">Modalidade</Th>
                      <Th className="text-fg">Status</Th>
                      <Th className="text-fg">Atualização</Th>
                      <Th align="right" className="text-fg">Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {planosAnaliseIniciada.slice(0, 8).map((item) => (
                      <Tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                        <Td>
                          <div className="font-semibold text-fg">{item.titulo || 'Não informado'}</div>
                          <div className="text-xs text-fg-muted">{item.numeroProtocolo || 'Sem protocolo'}</div>
                        </Td>
                        <Td className="text-fg">{item.emendaCodigo || 'Não informada'}</Td>
                        <Td className="text-fg">{modalidadeLabel(item.modalidade)}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${planoStatusTone(item.status)}`}>
                            {planoStatusLabel(item.status)}
                          </span>
                        </Td>
                        <Td className="text-fg">{formatDate(item.dataAtualizacao || item.aprovadoEm || item.enviadoEm)}</Td>
                        <Td align="right">
                          <Button variant="primary" size="sm" onClick={() => openAnaliseJuridica(item.id)}>
                            Continuar análise
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </div>
            </WorkspaceSection>
          )}
        </WorkspaceSectionGroup>
      )}
    </div>
  );
};

export default ProcuradoriaWorkspace;
